import React, { useEffect, useRef, useState } from 'react';
import YScaleProperties from '../../log-viewer/yscale-properties';
import Panel from '../panel';
import NumericInput from '../../numeric-input';
import { min } from 'lodash';

const DepthPanel = (props: any) => {
    const {
      depthType, 
      onChangeDepthType,      
      depthUnit,
      changeDepthUnit,            
      childLoading,
      setStartDepth,
      setEndDepth,
      maxDepth,
      minDepth,
      setSkipLoad,
      setShowDepthGridlines,
      majorIntervals,
      setMajorIntervals,
      showDepthGridLines,
      showDepthMinorIntervals,
      setShowDepthMinorIntervals,
      minorIntervals,
      setMinorIntervals,
      setManualDepthRange,
      startDepth,
      endDepth,
      setMouseWheelZoom,
      mouseWheelZoom,
      hasLoggedDepth
    } = props;    

    const [internalStartDepth, setInternalStartDepth] = useState<number | undefined>(startDepth ?? undefined);
    const [internalEndDepth, setInternalEndDepth] = useState<number | undefined>(endDepth ?? undefined);
    const [depthPerPage, setDepthPerPage] = useState<number>(0);
    const [startDepthError, setStartDepthError] = useState<any>();
    const [endDepthError, setEndDepthError] = useState<any>();
    // Tracks whether we are mid-reset so the prop-sync effects don't overwrite the new values
    const isResettingRef = useRef(false);
    const expectedAfterResetRef = useRef<{ start: number | undefined; end: number | undefined } | null>(null);

    useEffect(() => {
      if (internalEndDepth !== undefined && internalStartDepth !== undefined) {
        setDepthPerPage(roundToNearest(internalEndDepth - internalStartDepth));
      } else if (maxDepth !== undefined && minDepth !== undefined) {
        setDepthPerPage(roundToNearest(maxDepth - minDepth));
      } else {
        setDepthPerPage(0);
      }
    }, [internalEndDepth, internalStartDepth, maxDepth, minDepth]);

    // Keep internal values in sync with the chart's current depth window,
    // but only when we are not in the middle of a unit-change reset.
    useEffect(() => {
      if (isResettingRef.current) {
        // Unlock only when BOTH start and end have arrived at their expected values.
        if (
          expectedAfterResetRef.current &&
          startDepth === expectedAfterResetRef.current.start &&
          endDepth === expectedAfterResetRef.current.end
        ) {
          isResettingRef.current = false;
          expectedAfterResetRef.current = null;
        }
        return;
      }
      setInternalStartDepth(startDepth ?? undefined);
    }, [startDepth, endDepth]);

    useEffect(() => {
      if (isResettingRef.current) {
        if (
          expectedAfterResetRef.current &&
          startDepth === expectedAfterResetRef.current.start &&
          endDepth === expectedAfterResetRef.current.end
        ) {
          isResettingRef.current = false;
          expectedAfterResetRef.current = null;
        }
        return;
      }
      setInternalEndDepth(endDepth ?? undefined);
    }, [endDepth, startDepth]);

    const submit = (e: any) => {
      e.preventDefault();

      setStartDepthError(null);
      setEndDepthError(null);
      
      var errored = false;

      if (errored) {
        return;
      }

      setManualDepthRange(true);
      setStartDepth(internalStartDepth);      
      setEndDepth(internalEndDepth);
    };
    
    const convertDepthUnit = (value: number | undefined, fromUnit: number, toUnit: number) => {
      if (value === undefined || fromUnit === toUnit) return value;
      const factor = fromUnit === 1 && toUnit === 2
        ? 3.28084
        : (fromUnit === 2 && toUnit === 1 ? 1 / 3.28084 : 1);
      return roundToNearest(value * factor);
    };

    const applyDepthUnitChange = (nextUnit: number) => {
      if (nextUnit == null) return;

      const currentUnit = Number(depthUnit) === 2 ? 2 : 1;
      if (currentUnit === nextUnit) {
        changeDepthUnit(nextUnit);
        return;
      }

      const sourceStart = internalStartDepth ?? startDepth ?? undefined;
      const sourceEnd = internalEndDepth ?? endDepth ?? undefined;
      const convertedStart = convertDepthUnit(sourceStart, currentUnit, nextUnit);
      const convertedEnd = convertDepthUnit(sourceEnd, currentUnit, nextUnit);

      isResettingRef.current = true;
      expectedAfterResetRef.current = { start: convertedStart, end: convertedEnd };

      setInternalStartDepth(convertedStart);
      setInternalEndDepth(convertedEnd);
      if (convertedStart !== undefined && convertedEnd !== undefined) {
        setDepthPerPage(roundToNearest(convertedEnd - convertedStart));
      }

      setSkipLoad(true);
      changeDepthUnit(nextUnit);
      setStartDepth(convertedStart);
      setEndDepth(convertedEnd);
      setSkipLoad(false);
    };

    const setDepthUnitMeters = () => {
      applyDepthUnitChange(1);
    }

    const setDepthUnitFeet = () => {
      applyDepthUnitChange(2);
    }

    const onDepthPerPageChanged = (e: any) => {
      const depth = +e.target.value;
      setDepthPerPage(depth);
      setInternalEndDepth((internalStartDepth || 0) + depth);
    }

    const onStartDepthChanged = (depth: number | undefined) => {    
      setInternalStartDepth(depth);
      if (depth !== undefined && depthPerPage !== undefined && depthPerPage > 0) {
        const newEndDepth = roundToNearest(depth + depthPerPage);
        setInternalEndDepth(newEndDepth);
      }
    }

    const onEndDepthChanged = (depth: number | undefined) => {
      setInternalEndDepth(depth);
      if (depth !== undefined && internalStartDepth !== undefined) {
        setDepthPerPage(depth - internalStartDepth);
      }
    }

    const roundToNearest = (value: number) => {
      return Math.round(value * 1000) / 1000; 
    }

    const onPageDown = (e: any) => {      
      e.preventDefault();
      if (internalStartDepth === undefined || internalEndDepth === undefined) {
         return;
      }

      const pageSize = roundToNearest(internalEndDepth - internalStartDepth);
      if (pageSize <= 0) return;

      if (maxDepth !== undefined && internalEndDepth + pageSize > maxDepth) {
        return;
      }

      const newStartDepth = roundToNearest(internalStartDepth + pageSize);
      const newEndDepth = roundToNearest(internalEndDepth + pageSize);

      setInternalStartDepth(newStartDepth);
      setInternalEndDepth(newEndDepth);
      setSkipLoad(true);
      setStartDepth(newStartDepth);
      setEndDepth(newEndDepth);
      setSkipLoad(false);
    };

    const onPageUp = (e: any) => {
      e.preventDefault();
      if (internalStartDepth === undefined || internalEndDepth === undefined) {
        return;
      }

      const pageSize = roundToNearest(internalEndDepth - internalStartDepth);
      if (pageSize <= 0) return;

      const effectiveMinDepth = minDepth ?? -Infinity;
      if (internalStartDepth - pageSize < effectiveMinDepth) {
        return;
      }

      const newStartDepth = roundToNearest(internalStartDepth - pageSize);
      const newEndDepth = roundToNearest(internalEndDepth - pageSize);

      setInternalStartDepth(newStartDepth);
      setInternalEndDepth(newEndDepth);
      setSkipLoad(true);
      setStartDepth(newStartDepth);
      setEndDepth(newEndDepth);
      setSkipLoad(false);
    };

    const onReset = (e: any) => {
      e.preventDefault();
      reset(1);
      setManualDepthRange(false);   
    };

    const reset = (multiplier: number) => {
      const newMin = minDepth !== undefined ? minDepth * multiplier : undefined;
      const newMax = maxDepth !== undefined ? maxDepth * multiplier : undefined;

      isResettingRef.current = true;
      expectedAfterResetRef.current = { start: newMin, end: newMax };
      setInternalStartDepth(newMin);
      setInternalEndDepth(newMax);
      if (newMax !== undefined && newMin !== undefined) {
        setDepthPerPage(newMax - newMin);
      } else {
        setDepthPerPage(0);
      }
      setSkipLoad(true);
      setStartDepth(newMin);
      setEndDepth(newMax);
      setSkipLoad(false);
    };

return(
  <Panel title="Depth" icon="depth">
        <div className="panel-section">

 <div className=''>
                <div className='mouse-wheel-control-text'>
                  Mouse wheel Control:
                </div>
                <div className='mouse-wheel-control-control'>
                  <label className="mr-1" onClick={(e: any) => setMouseWheelZoom(true)}>zoom</label>                  

                  <input type="checkbox" className="switch is-rounded is-outlined" checked={!mouseWheelZoom} />
                  <label className="mouse-wheel-control-switch" onClick={(e: any) => setMouseWheelZoom(!mouseWheelZoom)}></label>

                  <label onClick={(e: any) => setMouseWheelZoom(false)}>scroll</label>
                </div>
            </div>

          <div className="field">
                <button className={`button mr-2 ${depthUnit == 1 ? "is-primary" : ""}`} onClick={setDepthUnitMeters}>Meters</button>
                <button className={`button ${depthUnit == 2 ? "is-primary" : ""}`} onClick={setDepthUnitFeet}>Feet</button>              
            </div>

            {hasLoggedDepth ? (
              <div className="field">
                  <label className="label">Depth Type</label>
                  <div className="control">
                    <div className="select">
                        <select onChange={onChangeDepthType} value={depthType}>                          
                            <option value={"Borehole"}>Borehole</option>
                            <option value={"Logged"}>Logged</option>                          
                        </select>
                    </div>
                  </div>
              </div>
            ) : (
              <div className="field">
                  <label className="label">Depth Type</label>
                  <div className="notification is-warning" style={{ padding: '0.75rem', marginTop: '0.5rem' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>No logged depth data available for this borehole</p>
                  </div>
              </div>
            )}
          </div>

          <div className="panel-section">
          <form onSubmit={submit}>   
            <div className="field">
                <label className="label">Start Depth</label>
                <div className="control">
                  <div className="depth-number-field">
                    <NumericInput className={`input ${startDepthError ? "is-danger" : ""}`} step="any" size={1} value={internalStartDepth ?? undefined} disabled={childLoading} onChange={onStartDepthChanged} />
                      {startDepthError && <div className='help is-danger'>{startDepthError}</div>}
                    </div>                  
                </div>
              </div>

              <div className="field">
                  <label className="label">{depthUnit == 1 ? "m" : "ft"} per page</label>
                  <div className="control">
                    <div className="field depth-number-field">
                        <input type="number" className="input" step="any" size={1} value={depthPerPage ?? undefined} disabled={childLoading} onChange={onDepthPerPageChanged} />
                      </div>
                      
                  </div>
              </div>

              <div className="field">
                  <label className="label">End Depth</label>
                  <div className="control">
                    <div className="field depth-number-field">
                        <NumericInput
                          className={`input ${endDepthError ? "is-danger" : ""}`}
                          step="any"
                          size={1}
                          value={internalEndDepth}
                          disabled={childLoading}
                          onChange={onEndDepthChanged}
                        />
                        {endDepthError && <div className='help is-danger'>{endDepthError}</div>}
                      </div>                  
                  </div>
              </div>

              <div>
                  <button className="button is-primary mr-2"  disabled={childLoading}>
                    OK
                  </button>              
                  <button className="button is-secondary" onClick={onReset}  disabled={childLoading}>
                    <i className="fas fa-undo" title="Reset"></i>&nbsp;Reset
                  </button>
              </div>   
            </form>          
          
            <div className="field mt-2">
                <label className="label">Next / Previous</label>
                <button className="button mr-2" onClick={onPageUp} disabled={childLoading}>
                  <i className="fas fa-arrow-up" title="Up"></i>
                </button>              
                <button className="button" onClick={onPageDown}  disabled={childLoading}>
                  <i className="fas fa-arrow-down" title="Down"></i>
                </button>
            </div>
            </div> 

          <div className="panel-section">
            <YScaleProperties
              setShowDepthGridlines={setShowDepthGridlines}
              majorIntervals={majorIntervals}
              setMajorIntervals={setMajorIntervals}
              showDepthGridLines={showDepthGridLines}
              showDepthMinorIntervals={showDepthMinorIntervals}
              setShowDepthMinorIntervals={setShowDepthMinorIntervals}
              minorIntervals={minorIntervals}
              setMinorIntervals={setMinorIntervals}
            />
          </div>
              
                 
      

  </Panel>
)};

export default DepthPanel;